/**
 * API Key Management Service (Shared)
 *
 * Platform-agnostic API key management.
 * Uses the environment adapter for accessing API keys.
 */

import { getEnv, hasEnv } from '../adapters/env';

class ApiKeyManager {
  constructor() {
    this.apiKeys = new Map();
    this.initialized = false;
  }

  /**
   * Initialize API keys from environment
   * Must be called after setEnvAdapter() is configured
   */
  initialize() {
    if (this.initialized) return;

    // Google Maps API Key (safe to expose - has domain restrictions)
    const googleMapsKey = getEnv('GOOGLE_MAPS_API_KEY');
    if (googleMapsKey) {
      this.apiKeys.set('google_maps', {
        key: googleMapsKey,
        type: 'public',
        restrictions: ['domain', 'referrer'],
        lastUsed: null,
      });
    }

    // Supabase Anon Key (safe to expose - has RLS protection)
    const supabaseAnonKey = getEnv('SUPABASE_ANON_KEY');
    if (supabaseAnonKey) {
      this.apiKeys.set('supabase_anon', {
        key: supabaseAnonKey,
        type: 'public',
        restrictions: ['rls'],
        lastUsed: null,
      });
    }

    // Stripe Publishable Key (safe to expose - has domain restrictions)
    const stripeTestKey = getEnv('STRIPE_TEST_PUBLISHABLE_KEY');
    const stripeLiveKey = getEnv('STRIPE_PUBLISHABLE_KEY');
    const stripePublishableKey = stripeTestKey || stripeLiveKey;

    if (stripePublishableKey) {
      this.apiKeys.set('stripe_publishable', {
        key: stripePublishableKey,
        type: 'public',
        restrictions: ['domain'],
        lastUsed: null,
        isTestMode: !!stripeTestKey,
      });
    }

    this.initialized = true;
  }

  /**
   * Ensure manager is initialized
   */
  ensureInitialized() {
    if (!this.initialized) {
      this.initialize();
    }
  }

  /**
   * Get API key by name
   */
  getApiKey(keyName) {
    this.ensureInitialized();
    const keyData = this.apiKeys.get(keyName);
    if (!keyData) {
      throw new Error(`API key '${keyName}' not found`);
    }

    // Update last used timestamp
    keyData.lastUsed = Date.now();
    return keyData.key;
  }

  /**
   * Check if API key exists
   */
  hasApiKey(keyName) {
    this.ensureInitialized();
    return this.apiKeys.has(keyName);
  }

  /**
   * Get all available API keys (metadata only)
   */
  getAllApiKeys() {
    this.ensureInitialized();
    const keys = {};
    this.apiKeys.forEach((keyData, keyName) => {
      keys[keyName] = {
        type: keyData.type,
        restrictions: keyData.restrictions,
        lastUsed: keyData.lastUsed,
      };
    });
    return keys;
  }

  /**
   * Validate API key format
   */
  validateApiKey(keyName, key) {
    const validators = {
      google_maps: (key) => /^AIza[0-9A-Za-z-_]{35}$/.test(key),
      supabase_anon: (key) =>
        /^eyJ[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*$/.test(key),
      stripe_publishable: (key) => /^pk_(test_|live_)[0-9a-zA-Z]{24,}$/.test(key),
    };

    const validator = validators[keyName];
    if (!validator) {
      return { valid: false, error: 'Unknown API key type' };
    }

    if (!validator(key)) {
      return { valid: false, error: 'Invalid API key format' };
    }

    return { valid: true };
  }

  /**
   * Get Google Maps API Key with validation
   */
  getGoogleMapsKey() {
    if (!this.hasApiKey('google_maps')) {
      throw new Error(
        'Google Maps API key not configured. Set GOOGLE_MAPS_API_KEY in your environment.'
      );
    }
    return this.getApiKey('google_maps');
  }

  /**
   * Get Supabase Anon Key with validation
   */
  getSupabaseAnonKey() {
    if (!this.hasApiKey('supabase_anon')) {
      throw new Error(
        'Supabase anon key not configured. Set SUPABASE_ANON_KEY in your environment.'
      );
    }
    return this.getApiKey('supabase_anon');
  }

  /**
   * Get Stripe Publishable Key with validation
   */
  getStripePublishableKey() {
    if (!this.hasApiKey('stripe_publishable')) {
      throw new Error(
        'Stripe publishable key not configured. Set STRIPE_PUBLISHABLE_KEY in your environment.'
      );
    }
    return this.getApiKey('stripe_publishable');
  }

  /**
   * Check if Stripe is in test mode
   */
  isStripeTestMode() {
    this.ensureInitialized();
    const keyData = this.apiKeys.get('stripe_publishable');
    return keyData?.isTestMode || false;
  }

  /**
   * Check API key health
   */
  async checkApiKeyHealth(keyName) {
    this.ensureInitialized();
    const keyData = this.apiKeys.get(keyName);
    if (!keyData) {
      return { healthy: false, error: 'Key not found' };
    }

    const validation = this.validateApiKey(keyName, keyData.key);
    if (!validation.valid) {
      return { healthy: false, error: validation.error };
    }

    return { healthy: true };
  }

  /**
   * Get API key usage statistics
   */
  getUsageStats() {
    this.ensureInitialized();
    const stats = {};
    this.apiKeys.forEach((keyData, keyName) => {
      stats[keyName] = {
        lastUsed: keyData.lastUsed,
        type: keyData.type,
        restrictions: keyData.restrictions,
      };
    });
    return stats;
  }

  /**
   * Reset manager (useful for testing or re-initialization)
   */
  reset() {
    this.apiKeys.clear();
    this.initialized = false;
  }
}

// Create singleton instance
const apiKeyManager = new ApiKeyManager();

export default apiKeyManager;

// Export individual methods for convenience
export const getApiKey = (...args) => apiKeyManager.getApiKey(...args);
export const hasApiKey = (...args) => apiKeyManager.hasApiKey(...args);
export const getAllApiKeys = (...args) => apiKeyManager.getAllApiKeys(...args);
export const validateApiKey = (...args) => apiKeyManager.validateApiKey(...args);
export const getGoogleMapsKey = (...args) => apiKeyManager.getGoogleMapsKey(...args);
export const getSupabaseAnonKey = (...args) => apiKeyManager.getSupabaseAnonKey(...args);
export const getStripePublishableKey = (...args) => apiKeyManager.getStripePublishableKey(...args);
export const isStripeTestMode = (...args) => apiKeyManager.isStripeTestMode(...args);
export const checkApiKeyHealth = (...args) => apiKeyManager.checkApiKeyHealth(...args);
export const getUsageStats = (...args) => apiKeyManager.getUsageStats(...args);
